import React, { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { COLORS } from "../constants";
import "./Revenue.css";

const Revenue = () => {
  const [metrics, setMetrics] = useState({
    activeSubscriptions: 0,
    premiumCount: 0,
    scholarCount: 0,
    monthlyRecurringRevenue: 0,
    orgMRR: 0,
    totalMRR: 0,
    orgStripeFees: 0,
    netOrgRevenue: 0,
    netIndividualRevenue: 0,
    individualMRR: 0,
    netRevenue: 0,
    activeOrgCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadCachedMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load cached metrics from analytics/revenue document
      const revenueDocRef = doc(db, "analytics", "revenue");
      const revenueDoc = await getDoc(revenueDocRef);

      if (revenueDoc.exists()) {
        const data = revenueDoc.data();
        setMetrics({
          activeSubscriptions: data.activeSubscriptions || 0,
          premiumCount: data.premiumCount || 0,
          scholarCount: data.scholarCount || 0,
          monthlyRecurringRevenue: data.monthlyRecurringRevenue || 0,
          orgMRR: data.orgMRR || 0,
          totalMRR: data.totalMRR || 0,
          orgStripeFees: data.orgStripeFees || 0,
          netOrgRevenue: data.netOrgRevenue || 0,
          netIndividualRevenue: data.netIndividualRevenue || 0,
          individualMRR: data.individualMRR || 0,
          netRevenue: data.netRevenue || 0,
          activeOrgCount: data.activeOrgCount || 0,
        });
        setLastUpdated(data.lastUpdated?.toDate() || null);
        console.log("Loaded cached revenue metrics from:", data.lastUpdated?.toDate());
      } else {
        console.log("No cached metrics found, calculating fresh...");
        await calculateAndSaveMetrics();
      }
    } catch (err) {
      console.error("Error loading cached metrics:", err);
      setError("Failed to load metrics. Please try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCachedMetrics();
  }, [loadCachedMetrics]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await calculateAndSaveMetrics();
    setIsRefreshing(false);
  };

  const calculateAndSaveMetrics = async () => {
    try {
      if (!isRefreshing) {
        setLoading(true);
      }
      setError(null);

      // Pull individual app revenue from Superwall webhook aggregates.
      // This matches Superwall's transaction event source instead of estimating
      // revenue from potentially stale Firestore entitlement fields.
      const functions = getFunctions();
      const getSuperwallIndividualRevenue = httpsCallable(
        functions,
        'getSuperwallIndividualRevenue'
      );
      const individualRevenueResult = await getSuperwallIndividualRevenue();
      const individualRevenueData = individualRevenueResult.data || {};

      const premiumCount = individualRevenueData.premiumCount || 0;
      const scholarCount = individualRevenueData.scholarCount || 0;
      const activeSubscriptions = individualRevenueData.activeSubscriptions || 0;
      const monthlyRecurringRevenue = individualRevenueData.grossRevenue || 0;
      const individualMRR = individualRevenueData.mrr || 0;
      const netIndividualRevenue = individualRevenueData.netRevenue || 0;

      console.log("=== Superwall Revenue Metrics Summary ===");
      console.log(`Premium subscribers: ${premiumCount}`);
      console.log(`Scholar subscribers: ${scholarCount}`);
      console.log(`Individual gross revenue this month: ${monthlyRecurringRevenue}`);
      console.log(`Individual MRR: ${individualMRR}`);
      console.log(`Individual net proceeds this month: ${netIndividualRevenue}`);

      // Get organization revenue from Cloud Function
      let orgMRR = 0;
      let orgStripeFees = 0;
      let netOrgRevenue = 0;
      let activeOrgCount = 0;

      try {
        const getOrganizationRevenue = httpsCallable(functions, 'getOrganizationRevenue');
        const orgRevenueResult = await getOrganizationRevenue();
        const orgRevenueData = orgRevenueResult.data;

        orgMRR = orgRevenueData.totalOrgMRR || 0;
        orgStripeFees = orgRevenueData.totalStripeFees || 0;
        netOrgRevenue = orgRevenueData.netOrgRevenue || 0;
        activeOrgCount = orgRevenueData.activeOrgCount || 0;

        console.log('Organization revenue:', {
          orgMRR,
          orgStripeFees,
          netOrgRevenue,
          activeOrgCount,
        });
      } catch (orgError) {
        console.error('Error fetching organization revenue:', orgError);
        // Continue without org revenue if there's an error
      }

      // Total monthly revenue combines actual Superwall individual revenue for
      // the current month and monthly-equivalent organization recurring revenue.
      const totalMRR = monthlyRecurringRevenue + orgMRR;
      const netRevenue = netIndividualRevenue + netOrgRevenue;

      const metricsData = {
        activeSubscriptions,
        premiumCount,
        scholarCount,
        monthlyRecurringRevenue,
        orgMRR,
        totalMRR,
        orgStripeFees,
        netOrgRevenue,
        netIndividualRevenue,
        individualMRR,
        netRevenue,
        activeOrgCount,
        lastUpdated: new Date(),
      };

      setMetrics({
        activeSubscriptions,
        premiumCount,
        scholarCount,
        monthlyRecurringRevenue,
        orgMRR,
        totalMRR,
        orgStripeFees,
        netOrgRevenue,
        netIndividualRevenue,
        individualMRR,
        netRevenue,
        activeOrgCount,
      });

      const now = new Date();
      setLastUpdated(now);

      // Save metrics to analytics/revenue document
      const revenueDocRef = doc(db, "analytics", "revenue");
      await setDoc(revenueDocRef, {
        ...metricsData,
        lastUpdated: now,
      });

      console.log("✅ Revenue metrics saved to analytics/revenue");
    } catch (err) {
      console.error("Error calculating revenue metrics:", err);
      setError("Failed to calculate revenue metrics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="revenue-page">
        <div className="page-header">
          <h1>Revenue</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading revenue data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="revenue-page">
        <div className="page-header">
          <h1>Revenue</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadCachedMetrics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Data for pie chart (include organizations if they exist)
  const pieData = [
    {
      name: "Premium ($4.99/mo)",
      value: metrics.premiumCount,
      color: COLORS.primary,
    },
    {
      name: "Scholar ($9.99/mo)",
      value: metrics.scholarCount,
      color: COLORS.accent,
    },
  ];

  // Add organization revenue to pie chart if it exists
  if (metrics.orgMRR > 0) {
    pieData.push({
      name: `Organizations (${metrics.activeOrgCount} orgs)`,
      value: metrics.activeOrgCount,
      color: "#4CAF50",
    });
  }

  const formatLastUpdated = () => {
    if (!lastUpdated) return "";

    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  return (
    <div className="revenue-page">
      <div className="page-header">
        <div>
          <h1>Revenue</h1>
          {lastUpdated && (
            <p className="last-updated">Last updated: {formatLastUpdated()}</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleRefresh}
            className="refresh-button"
            disabled={isRefreshing}
          >
            {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      <div className="revenue-grid">
        <div className="revenue-card full-width">
          <h3 className="card-title">Subscription Overview</h3>
          <div className="subscription-overview">
            <div className="overview-metric">
              <div
                className="metric-value large"
                style={{ color: COLORS.primary }}
              >
                {metrics.activeSubscriptions + metrics.activeOrgCount}
              </div>
              <div className="metric-label">Total Active Subscriptions</div>
            </div>
            <div className="overview-metric">
              <div className="metric-value" style={{ color: COLORS.primary }}>
                {metrics.premiumCount}
              </div>
              <div className="metric-label">Premium Plans</div>
            </div>
            <div className="overview-metric">
              <div className="metric-value" style={{ color: COLORS.accent }}>
                {metrics.scholarCount}
              </div>
              <div className="metric-label">Scholar Plans</div>
            </div>
            {metrics.activeOrgCount > 0 && (
              <div className="overview-metric">
                <div className="metric-value" style={{ color: "#4CAF50" }}>
                  {metrics.activeOrgCount}
                </div>
                <div className="metric-label">Organizations</div>
              </div>
            )}
          </div>
        </div>

        <div className="revenue-card">
          <h3 className="card-title">Plan Distribution</h3>
          {metrics.activeSubscriptions > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-chart">
              <p>No active subscriptions</p>
            </div>
          )}
        </div>

        <div className="revenue-card">
          <h3 className="card-title">Total Monthly Revenue</h3>
          <div className="revenue-breakdown">
            <div className="revenue-item highlight">
              <div className="revenue-label">Total Monthly Revenue</div>
              <div className="revenue-amount large" style={{ color: COLORS.primary }}>
                ${metrics.totalMRR.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <div className="revenue-card">
          <h3 className="card-title">Revenue Breakdown</h3>
          <div className="revenue-breakdown">
            <div className="revenue-section">
              <div className="revenue-section-title">Individual Subscriptions</div>
              <div className="revenue-item">
                <div className="revenue-label">Superwall Gross Revenue This Month</div>
                <div className="revenue-amount" style={{ color: COLORS.primary }}>
                  ${metrics.monthlyRecurringRevenue.toFixed(2)}
                </div>
              </div>
              {metrics.individualMRR > 0 && (
                <>
                  <div className="revenue-divider"></div>
                  <div className="revenue-item">
                    <div className="revenue-label">Superwall MRR</div>
                    <div className="revenue-amount" style={{ color: COLORS.primary }}>
                      ${metrics.individualMRR.toFixed(2)}
                    </div>
                  </div>
                </>
              )}
              <div className="revenue-divider"></div>
              <div className="revenue-item">
                <div className="revenue-label">Superwall Net Proceeds This Month</div>
                <div className="revenue-amount" style={{ color: COLORS.primary }}>
                  ${metrics.netIndividualRevenue.toFixed(2)}
                </div>
              </div>
            </div>

            {metrics.orgMRR > 0 && (
              <>
                <div className="revenue-divider thick"></div>
                <div className="revenue-section">
                  <div className="revenue-section-title">Organization Subscriptions</div>
                  <div className="revenue-item">
                    <div className="revenue-label">
                      Organization Subscriptions MRR
                      <span className="revenue-note">
                        ({metrics.activeOrgCount} active orgs)
                      </span>
                    </div>
                    <div className="revenue-amount" style={{ color: COLORS.accent }}>
                      ${metrics.orgMRR.toFixed(2)}
                    </div>
                  </div>
                  <div className="revenue-divider"></div>
                  <div className="revenue-item">
                    <div className="revenue-label">
                      Stripe Fees
                      <span className="revenue-note">(2.9% + $0.30 per invoice)</span>
                    </div>
                    <div className="revenue-amount negative">
                      -${metrics.orgStripeFees.toFixed(2)}
                    </div>
                  </div>
                  <div className="revenue-divider"></div>
                  <div className="revenue-item">
                    <div className="revenue-label">Net Organization Revenue</div>
                    <div className="revenue-amount" style={{ color: COLORS.accent }}>
                      ${metrics.netOrgRevenue.toFixed(2)}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="revenue-divider thick"></div>
            <div className="revenue-item highlight">
              <div className="revenue-label">Total Net Revenue</div>
              <div className="revenue-amount large" style={{ color: COLORS.success }}>
                ${metrics.netRevenue.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Revenue;
